// store/pieces/createPiecesSlice.ts
import { StateCreator } from 'zustand';
import { piecesInitialState, PiecesState } from './piecesInitialState';
import { DetailedPiece, EcritureHistory, Piece, PiecesQueryParams } from '../../types';
import { createFunctionalSetter } from '../storeCommons';
import { TypeMenu } from '../../enums/piecesEnums';

/**
 * @french
 * Interface des actions liées aux pièces comptables.
 */
export interface PiecesActions {
  setQueryParams: (queryParams: PiecesQueryParams | null) => void;
  setPieceIndex: (index: number) => void;
  setPiece: (piece: Piece) => void;
  setTypeMenu: (type: TypeMenu) => void;
  setDetailedPiece: (
    updater:
      | DetailedPiece
      | undefined
      | ((prev: DetailedPiece | undefined) => DetailedPiece | undefined),
  ) => void;
  setIsEcritureLoading: (loading: boolean) => void;
  setIsPiecesLoading: (loading: boolean) => void;
  setIsListPieceEmpty: (loading: boolean) => void;
  setEcritureHistory: (history: EcritureHistory[]) => void;
  setIsChangeJournalActive: (state: boolean) => void;

  /** 🔹 Actions de gestion de la prévisualisation */
  setPreview: (
    updater: PiecesState['preview'] | ((prev: PiecesState['preview']) => PiecesState['preview']),
  ) => void;
  resetPreview: () => void;

  reset: () => void;
}

/**
 * @french
 * Slice Zustand pour les actions liées aux pièces comptables.
 */
export const createPiecesSlice: StateCreator<PiecesState & PiecesActions, [], [], PiecesActions> = (
  set,
  get,
) => ({
  setQueryParams: (queryParams) => set({ queryParams }),
  setTypeMenu: (type) => set({ typeMenu: type }),
  setPieceIndex: (index) => set({ pieceIndex: index }),
  setPiece: (piece) => set({ piece }),
  setDetailedPiece: createFunctionalSetter(get, set, 'detailedPiece'),
  setIsEcritureLoading: (loading) => set({ isEcritureLoading: loading }),
  setIsPiecesLoading: (loading) => set({ isPiecesLoading: loading }),
  setIsListPieceEmpty: (loading) => set({ isListPieceEmpty: loading }),
  setEcritureHistory: (history) => set({ ecritureHistory: history }),
  setIsChangeJournalActive: (state) => set({ isChangeJournalActive: state }),

  // 🔹 Gestion centralisée de la prévisualisation
  setPreview: (updater) =>
    set((state) => ({
      preview: typeof updater === 'function' ? updater(state.preview) : updater,
    })),

  resetPreview: () =>
    set((state) => ({
      preview: { ...piecesInitialState.preview },
    })),

  reset: () => set(piecesInitialState),
});
